import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Icon } from '@material-ui/core';
import Button from '../../atoms/Button';
import Checkbox from '../../atoms/Checkbox';
import IconButton from '../../atoms/IconButton';
import Textbox from '../../atoms/Textbox';
import { CATEGORY_VALIDATION, getFindIndex, getSpecifiedCondition, SALE_MANCD_NAME_KEY } from '../../../Common';
import { AppState } from '../../../redux/modules/store';
import {
  LargeCategory,
  MiddleCategory,
  SearchCategoryState,
  initialState as initialStateSearchCategory,
} from '../../../redux/reducers/SearchCategoryReducer';
import { SearchConditionItem, SearchConditionState } from '../../../redux/reducers/SearchConditionReducer';
import { convertSearchCategory, getSearchConditionListWithoutNoInput, getSmallCategoriesWithoutActionNoInput } from './function';
import { cloneDeep } from 'lodash';
import { GASpotEventState } from '../NegotiationCountList';

interface FilterSettingModalProps {
  isOpen: boolean;
  gaSpotEvent?: { [key: string]: GASpotEventState };
  onClose: () => void;
  searchCondition: SearchConditionState;
  onAddFilter: (searchCondition: SearchConditionState) => void;
  isSearchImprovement?: boolean;
  searchCategoryFetch?: SearchCategoryState;
}

const FilterSettingModal: React.FC<FilterSettingModalProps> = ({
  isOpen,
  gaSpotEvent,
  onClose,
  searchCondition,
  onAddFilter,
  isSearchImprovement,
  searchCategoryFetch,
}) => {
  const loginUser = useSelector((state: AppState) => state.loginUser);
  const [selectedSearchCondition, setSelectedSearchCondition] = useState<SearchConditionState>([]);
  const [selectedLargeCatergoryCd, setSelectedLargeCatergoryCd] = useState<string>('');
  const [selectedMiddleCatergoryCd, setSelectedMiddleCatergoryCd] = useState<string>('');
  const [searchCategory, setSearchCategory] = useState<SearchCategoryState>(initialStateSearchCategory);
  const [searchCategoryCopy, setSearchCategoryCopy] = useState<SearchCategoryState>(initialStateSearchCategory);
  const onOpenModal = async () => {
    let activeLargeCatergoryCd = '';
    let activeMiddleCatergoryCd = '';
    if (searchCategoryFetch && searchCategoryFetch.categories.length != 0) {
      setSearchCategoryCopy(searchCategoryFetch);
      setSearchCategory(convertSearchCategory(cloneDeep(searchCategoryFetch)));
      const activeLargeCatergory: LargeCategory = searchCategoryFetch.categories[0];
      activeLargeCatergoryCd = activeLargeCatergory.largeCategoryCd;
      if (activeLargeCatergory.middleCategory.length != 0) {
        activeMiddleCatergoryCd = activeLargeCatergory.middleCategory[0].middleCategoryCd;
      }
    }
    const searchConditionCopy: SearchConditionState = [...searchCondition];
    setSelectedSearchCondition(getSearchConditionListWithoutNoInput(searchConditionCopy));
    setSelectedLargeCatergoryCd(activeLargeCatergoryCd);
    setSelectedMiddleCatergoryCd(activeMiddleCatergoryCd);
  };

  const changeLargeCategory = (activeLargeCategory: LargeCategory) => {
    let activeMiddleCatergoryCd = '';
    if (activeLargeCategory.middleCategory.length > 0) {
      activeMiddleCatergoryCd = activeLargeCategory.middleCategory[0].middleCategoryCd;
    }
    setSelectedLargeCatergoryCd(activeLargeCategory.largeCategoryCd);
    setSelectedMiddleCatergoryCd(activeMiddleCatergoryCd);
  };

  const calcLargeCount = (largeCategory: LargeCategory): number => {
    let largeCount = 0;
    largeCategory.middleCategory.forEach((middleCategory) => {
      const index = getFindIndex(selectedSearchCondition, middleCategory.middleCategoryCdMapping);
      if (index !== -1 && !!selectedSearchCondition[index]?.specifiedCondition.length) {
        largeCount++;
      }
    });
    return largeCount;
  };

  const calcMiddleCount = (middleCategoryCdMapping: string): number => {
    if (getFindIndex(selectedSearchCondition, middleCategoryCdMapping) !== -1) {
      return getSpecifiedCondition(selectedSearchCondition, middleCategoryCdMapping).length;
    } else {
      return 0;
    }
  };

  const updateSelectedSearchCondition = (
    selectedSearchCondition: SearchConditionState,
    mapping: string,
    value: string,
    checked: boolean,
  ): SearchConditionState => {
    let selectedSearchConditionCopy: SearchConditionState = [...selectedSearchCondition];
    const findIndex = getFindIndex(selectedSearchConditionCopy, mapping);
    let specifiedCondition: (string | null)[] = [];

    if (checked) {
      // 1. チェックを付ける
      if (findIndex !== -1) {
        specifiedCondition = selectedSearchConditionCopy[findIndex].specifiedCondition;
        // 1-1. 既存検索条件に存在する場合
        if (getSpecifiedCondition(selectedSearchConditionCopy, mapping).filter((v) => v == value).length === 0) {
          // 1-1-1. 追加する指定条件が存在する場合
          specifiedCondition.push(value);
        }
        // 1-2. 検索指定条件を更新する
        selectedSearchConditionCopy[findIndex] = {
          ...selectedSearchConditionCopy[findIndex],
          specifiedCondition: specifiedCondition,
        };
      } else {
        const addSearchConditionItem: SearchConditionItem = {
          searchConditionName: mapping,
          specifiedCondition: [value],
        };
        selectedSearchConditionCopy.push(addSearchConditionItem);
      }
    } else {
      // 2. チェックを外す
      if (findIndex !== -1) {
        // 1-1. 既存検索条件に存在する場合
        specifiedCondition = selectedSearchConditionCopy[findIndex].specifiedCondition;
        // チェックを外した検索指定条件以外の指定条件があるか確認する。
        const newValues = specifiedCondition.filter((i) => value !== i).map((value) => value);

        if (newValues.length === 0) {
          const filterCondition = selectedSearchConditionCopy.filter((searchCondition) => searchCondition.searchConditionName !== mapping);
          selectedSearchConditionCopy = filterCondition;
        } else {
          // 検索指定条件を更新する
          selectedSearchConditionCopy[findIndex] = {
            ...selectedSearchConditionCopy[findIndex],
            specifiedCondition: newValues,
          };
        }
      }
    }

    return selectedSearchConditionCopy;
  };

  // get the value of small category name according to crew code
  const getSmallCategoryCdName = (key: string) => {
    return (
      searchCategoryCopy?.categories[1]?.middleCategory
        .find((item) => item.middleCategoryCdMapping === key)
        ?.smallCategory?.find((item) => item.smallCategoryCd === loginUser.crewCd)?.smallCategoryCdName || ''
    );
  };

  // common function to perform logic processing corresponding to 2 newly added checkboxes outside filterSettingDrawer screen
  const addCheckedKey = (arr: SearchConditionState, key: string, hasSmallCategoryCdName: boolean) => {
    return arr.map((item) => {
      if (item && item.searchConditionName === key) {
        const isChecked = (!hasSmallCategoryCdName && !item.specifiedCondition.length && item.checked) || false;
        return { ...item, checked: (item.specifiedCondition.length === 1 && hasSmallCategoryCdName) || isChecked };
      }
      return item;
    });
  };

  // check if it is the key to perform checkbox on Crew module
  const isSaleManCdKeyOfCrew = (key: string) => {
    return isSearchImprovement && (key === SALE_MANCD_NAME_KEY.CLOSING_SALE_MANCD || key === SALE_MANCD_NAME_KEY.MAIN_CLOSING_SALE_MANCD);
  };

  const onSelectItem = (mapping: string, value: string, checked: boolean) => {
    const selectedSearchConditionCopy: SearchConditionState = [...selectedSearchCondition];
    // check if it is being performed on the key or not, small category name corresponding to crew code to process
    const haveKeyatCrew = isSaleManCdKeyOfCrew(mapping);
    if (haveKeyatCrew) {
      const tempCondition = [...updateSelectedSearchCondition(selectedSearchConditionCopy, mapping, value, checked)];
      const hasSmallCategoryCdName =
        tempCondition.find((item) => item.searchConditionName === mapping)?.specifiedCondition.includes(getSmallCategoryCdName(mapping)) ||
        false;
      const result = addCheckedKey(tempCondition, mapping, hasSmallCategoryCdName);
      setSelectedSearchCondition(result);
    } else setSelectedSearchCondition(updateSelectedSearchCondition(selectedSearchConditionCopy, mapping, value, checked));
  };

  const setAllCheck = (middleCategory: MiddleCategory, checked: boolean, largeCategory: LargeCategory): void => {
    let selectedSearchConditionCopy: SearchConditionState = [...selectedSearchCondition];

    const middleCategoryCdMapping = middleCategory.middleCategoryCdMapping;
    const smallCategoryList = getSmallCategoriesWithoutActionNoInput(middleCategory, largeCategory);
    // check if small category name is already in smallCategoryList
    const mappingType = middleCategory.middleCategoryCdMapping;
    const hasSmallCategoryCdName = smallCategoryList.some((item) => item.smallCategoryCdName === getSmallCategoryCdName(mappingType));
    for (let i = 0; i < smallCategoryList.length; i++) {
      selectedSearchConditionCopy = updateSelectedSearchCondition(
        selectedSearchConditionCopy,
        middleCategoryCdMapping,
        smallCategoryList[i].smallCategoryCd,
        checked,
      );
    }

    // preprocess data search condition when checkall to the key containing crew code
    if (isSaleManCdKeyOfCrew(middleCategory.middleCategoryCdMapping)) {
      selectedSearchConditionCopy = addCheckedKey(
        selectedSearchConditionCopy,
        middleCategory.middleCategoryCdMapping,
        hasSmallCategoryCdName,
      );
    }

    setSelectedSearchCondition(selectedSearchConditionCopy);
  };

  const getChecked = (mapping: string, value: string): boolean => {
    const findIndex = getFindIndex(selectedSearchCondition, mapping);
    if (findIndex !== -1) {
      return selectedSearchCondition[findIndex].specifiedCondition.some((v) => v === value);
    }
    return false;
  };

  const isAllChecked = (middleCategory: MiddleCategory, largeCategory: LargeCategory): boolean => {
    const middleCategoryCdMapping = middleCategory.middleCategoryCdMapping;
    const smallCategoryList = getSmallCategoriesWithoutActionNoInput(middleCategory, largeCategory);

    for (let i = 0; i < smallCategoryList.length; i++) {
      if (!getChecked(middleCategoryCdMapping, smallCategoryList[i].smallCategoryCd)) {
        return false;
      }
    }

    return true;
  };

  const getText = (mappingName: string): string => {
    const findIndex = getFindIndex(selectedSearchCondition, mappingName);
    let mappingText: string;

    if (findIndex !== -1) {
      mappingText = getSpecifiedCondition(selectedSearchCondition, mappingName)[0] || '';
    } else {
      mappingText = '';
    }
    return mappingText;
  };

  const onChangeText = (middleCategory: MiddleCategory, specifiedCondition: string) => {
    let selectedSearchConditionCopy: SearchConditionState = [...selectedSearchCondition];
    const findIndex = getFindIndex(selectedSearchConditionCopy, middleCategory.middleCategoryCdMapping);

    if (validCheck(middleCategory.smallCategoryAttribute, specifiedCondition)) {
      if (findIndex !== -1) {
        if (specifiedCondition) {
          // 1-1. テキスト：検索詳細条件を更新する。
          selectedSearchConditionCopy[findIndex] = {
            ...selectedSearchConditionCopy[findIndex],
            specifiedCondition: [specifiedCondition],
          };
        } else {
          // 1-2. テキスト：検索詳細条件を削除する。
          const filterCondition = selectedSearchConditionCopy.filter(
            (searchCondition) => searchCondition.searchConditionName !== middleCategory.middleCategoryCdMapping,
          );
          selectedSearchConditionCopy = filterCondition;
        }
      } else {
        // 2. テキスト：検索詳細条件を追加する。
        if (specifiedCondition) {
          const addSearchConditionItem: SearchConditionItem = {
            searchConditionName: middleCategory.middleCategoryCdMapping,
            specifiedCondition: [specifiedCondition],
          };
          selectedSearchConditionCopy.push(addSearchConditionItem);
        }
      }
    }
    setSelectedSearchCondition(selectedSearchConditionCopy);
  };

  const validCheck = (smallCategoryAttribute: string, specifiedCondition: string): boolean => {
    if (CATEGORY_VALIDATION[smallCategoryAttribute]) {
      return CATEGORY_VALIDATION[smallCategoryAttribute].validation.test(specifiedCondition);
    }
    return true;
  };

  const getInputMessage = (smallCategoryAttribute: string): string => {
    if (CATEGORY_VALIDATION[smallCategoryAttribute]) {
      return CATEGORY_VALIDATION[smallCategoryAttribute].editMessage;
    }
    return '';
  };

  const isSelector = (smallCategoryAttribute: string): boolean => {
    switch (smallCategoryAttribute) {
      case 'checkbox':
      case 'pulldown':
      case 'radio':
        return true;

      default:
        return false;
    }
  };

  const isDecimal = (smallCategoryAttribute: string): boolean => smallCategoryAttribute === 'decimal';

  const onAdd = () => {
    onAddFilter(selectedSearchCondition);
    onClose();
  };

  useEffect(() => {
    isOpen && onOpenModal();
  }, [isOpen]);

  useEffect(() => {
    if (gaSpotEvent) {
      Object.values(gaSpotEvent).forEach(({ spotEvent, className }) => {
        document.querySelector(`.${className}`)?.setAttribute('onclick', spotEvent);
      });
    }
  }, []);

  const closeSpotEvent = gaSpotEvent?.close;
  const cancelSpotEvent = gaSpotEvent?.cancel;
  const addSpotEvent = gaSpotEvent?.add;

  return (
    <div className={classNames('filter-modal uc-modal', isOpen && 'uc-modal--open')}>
      <div className='uc-modal__overlay' onClick={() => onClose()} data-testid='close-overlay'></div>
      <div className='uc-modal__container'>
        <div className='uc-modal__surface'>
          <div className='uc-modal__header'>
            <h2 className='uc-modal__header-title'>条件の追加</h2>
            <IconButton
              onClick={() => onClose()}
              className={classNames('uc-modal__close-button', closeSpotEvent ? closeSpotEvent.className : '')}
              icon='close'
              data-testid='close-icon'
            />
          </div>
          <hr className='uc-divider' />
          <div className='uc-modal__content u-flex'>
            <div className='filter-modal__left'>
              {/* 1階層目 */}
              <div className='filter-modal__scroll-area'>
                <ul className='list'>
                  {searchCategory.categories.map((largeCategory) => (
                    <li
                      key={largeCategory.largeCategoryCd}
                      className={classNames(
                        'list__item-link list__item-link--has-right-icon',
                        largeCategory.largeCategoryCd === selectedLargeCatergoryCd && 'list__item-link--active',
                      )}>
                      <Link to='#' onClick={() => changeLargeCategory(largeCategory)} data-testid='large-category-name'>
                        {largeCategory.largeCategoryCdName}
                        {calcLargeCount(largeCategory) > 0 && (
                          <span
                            className='uc-badge uc-badge--info u-text-middle u-mln8 u-mr8'
                            style={{ position: 'absolute', right: '20px' }}
                            data-testid='large-category-count'>
                            {calcLargeCount(largeCategory)}
                          </span>
                        )}
                      </Link>
                      <Icon className='list__right-icon'>keyboard_arrow_right</Icon>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className='filter-modal__center'>
              {/* 2階層目 */}
              <div className='filter-modal__scroll-area'>
                <ul className='list'>
                  {searchCategory.categories
                    .filter((largeCategory) => largeCategory.largeCategoryCd === selectedLargeCatergoryCd)
                    .map((largeCategory) =>
                      largeCategory.middleCategory.map((middleCategory) => (
                        <li
                          className={classNames(
                            'list__item-link list__item-link--has-right-icon',
                            middleCategory.middleCategoryCd === selectedMiddleCatergoryCd && 'list__item-link--active',
                          )}
                          key={`${largeCategory.largeCategoryCd}-${middleCategory.middleCategoryCd}`}>
                          <Link
                            to='#'
                            onClick={() => setSelectedMiddleCatergoryCd(middleCategory.middleCategoryCd)}
                            style={{ paddingRight: '54px' }}
                            data-testid='middle-category-name'>
                            {middleCategory.middleCategoryCdName}
                            {calcMiddleCount(middleCategory.middleCategoryCdMapping) > 0 && (
                              <span
                                className='uc-badge uc-badge--info u-text-middle u-mln8 u-mr8'
                                style={{
                                  position: 'absolute',
                                  right: '20px',
                                }}
                                data-testid='middle-category-count'>
                                {calcMiddleCount(middleCategory.middleCategoryCdMapping)}
                              </span>
                            )}
                          </Link>
                          <Icon className='list__right-icon'>keyboard_arrow_right</Icon>
                        </li>
                      )),
                    )}
                </ul>
              </div>
            </div>
            <div className='filter-modal__right'>
              {/* 3階層目 */}
              <div className='filter-modal__scroll-area'>
                <ul className='list'>
                  {searchCategory.categories
                    .filter((largeCategory) => largeCategory.largeCategoryCd === selectedLargeCatergoryCd)
                    .map((largeCategory) =>
                      largeCategory.middleCategory
                        .filter((middleCategory) => middleCategory.middleCategoryCd === selectedMiddleCatergoryCd)
                        .map((middleCategory) => {
                          if (middleCategory.smallCategory && isSelector(middleCategory.smallCategoryAttribute)) {
                            const smallCategories = getSmallCategoriesWithoutActionNoInput(middleCategory, largeCategory);
                            return (
                              <>
                                <li>
                                  <Checkbox
                                    data-testid='small-category-checkbox-all'
                                    width='full'
                                    className='u-ml'
                                    labelClassName='u-mv8 u-h-auto'
                                    label='すべて選択'
                                    onChange={(e) => {
                                      setAllCheck(middleCategory, e.target.checked, largeCategory);
                                    }}
                                    checked={isAllChecked(middleCategory, largeCategory)}
                                  />
                                </li>
                                {smallCategories.map((smallCategory, small_i) => (
                                  <li
                                    key={`${largeCategory.largeCategoryCd}-${middleCategory.middleCategoryCd}-${small_i}`}
                                    className={classNames('list__item-link', 'list__item-link--has-right-icon')}
                                    data-testid='small-category-name'>
                                    <Checkbox
                                      data-testid='small-category-checkbox'
                                      width='full'
                                      className='u-ml'
                                      labelClassName='u-mv8 u-h-auto'
                                      label={smallCategory.smallCategoryCdName}
                                      name='example-checkbox'
                                      onChange={(e) =>
                                        onSelectItem(
                                          middleCategory.middleCategoryCdMapping,
                                          smallCategory.smallCategoryCd,
                                          e.target.checked,
                                        )
                                      }
                                      value={smallCategory.smallCategoryCd}
                                      checked={getChecked(middleCategory.middleCategoryCdMapping, smallCategory.smallCategoryCd)}
                                    />
                                  </li>
                                ))}
                              </>
                            );
                          } else if (
                            // 数字系項目入力時のキーボード挙動変更対応
                            isDecimal(middleCategory.smallCategoryAttribute)
                          ) {
                            return (
                              <>
                                <li
                                  key={middleCategory.middleCategoryCdMapping}
                                  className={classNames('list__item-link', 'list__item-link--has-right-icon')}
                                  data-testid='small-category-name'>
                                  <Textbox
                                    wrapperClass='filter-modal__textbox u-w-full'
                                    width='full'
                                    type='tel'
                                    value={getText(middleCategory.middleCategoryCdMapping)}
                                    onChange={(e) => onChangeText(middleCategory, e.target.value)}
                                    placeholder={middleCategory.middleCategoryCdName}
                                    data-testid='small-category-text'
                                  />
                                </li>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <li
                                  key={middleCategory.middleCategoryCdMapping}
                                  className={classNames('list__item-link', 'list__item-link--has-right-icon')}
                                  data-testid='small-category-name'>
                                  <Textbox
                                    wrapperClass='filter-modal__textbox u-w-full'
                                    width='full'
                                    value={getText(middleCategory.middleCategoryCdMapping)}
                                    onChange={(e) => onChangeText(middleCategory, e.target.value)}
                                    placeholder={middleCategory.middleCategoryCdName}
                                    data-testid='small-category-text'
                                  />
                                </li>
                                {getInputMessage(middleCategory.smallCategoryAttribute) && (
                                  <li key={`${middleCategory.middleCategoryCdMapping}-explan`} data-testid='small-category-text-explan'>
                                    {getInputMessage(middleCategory.smallCategoryAttribute)}
                                  </li>
                                )}
                              </>
                            );
                          }
                        }),
                    )}
                </ul>
              </div>
            </div>
          </div>
          <hr className='uc-divider' />
          <div className='uc-modal__actions'>
            <Button
              className={classNames(cancelSpotEvent ? cancelSpotEvent.className : '')}
              type='secondary'
              label='キャンセル'
              onClick={() => onClose()}
            />
            <Button
              className={classNames(addSpotEvent ? addSpotEvent.className : '')}
              label='追加'
              onClick={onAdd}
              data-testid='category-add-button'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSettingModal;
